import { useState, useEffect, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useApiClient } from "../hooks/use-api-client.ts";
import { useScanner } from "../hooks/use-scanner.ts";
import type { PickingSummary } from "../api/client.ts";

interface Props {
  workflow: "receipt" | "dispatch";
  title: string;
  /** Tailwind accent, e.g. "blue" or "green". */
  accent: "blue" | "green";
  placeholder: string;
}

/**
 * Shared "select an open picking from a list" screen for the receipt and dispatch
 * workflows. Loads open pickings of the workflow's type, lets the operator tap one,
 * and falls back to manual entry / scanning a paper picking list.
 */
export function PickingPicker({ workflow, title, accent, placeholder }: Props) {
  const navigate = useNavigate();
  const api = useApiClient();
  const [ref, setRef] = useState("");
  const [pickings, setPickings] = useState<PickingSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showManual, setShowManual] = useState(false);

  const detailRoute = workflow === "receipt" ? "/receipt/$ref" : "/dispatch/$ref";
  const btn = accent === "green" ? "bg-green-600 active:bg-green-700" : "bg-blue-600 active:bg-blue-700";
  const focus = accent === "green" ? "focus:border-green-500" : "focus:border-blue-500";

  useEffect(() => {
    let active = true;
    setLoadError(null);
    api
      .listPickings(workflow)
      .then((r) => {
        if (active) setPickings(r.pickings);
      })
      .catch((e: unknown) => {
        if (active) setLoadError(e instanceof Error ? e.message : "Could not load pickings");
      });
    return () => {
      active = false;
    };
  }, [api, workflow]);

  useScanner((event) => {
    const trimmed = event.rawBarcode.trim().toUpperCase();
    if (trimmed) void navigate({ to: detailRoute, params: { ref: trimmed } });
  });

  function open(value: string) {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) return;
    void navigate({ to: detailRoute, params: { ref: trimmed } });
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    open(ref);
  }

  return (
    <div className="flex flex-1 flex-col p-6">
      <button
        onClick={() => void navigate({ to: "/workflow" })}
        className="mb-6 flex w-fit items-center gap-1 text-sm text-gray-400 active:text-white"
      >
        ← Back
      </button>
      <h2 className="mb-2 text-2xl font-bold">{title}</h2>
      <p className="mb-6 text-gray-400">Select a picking</p>

      {pickings === null && !loadError && (
        <p className="py-8 text-center text-gray-500">Loading…</p>
      )}
      {loadError && (
        <div className="mb-4 rounded-xl border border-red-700 bg-red-950/40 p-4 text-sm text-red-300">
          Could not load pickings: {loadError}
        </div>
      )}
      {pickings !== null && pickings.length === 0 && !loadError && (
        <p className="py-8 text-center text-gray-500">No open pickings found.</p>
      )}
      {pickings !== null && pickings.length > 0 && (
        <ul className="space-y-2">
          {pickings.map((p) => (
            <li key={p.name}>
              <button
                onClick={() => open(p.name)}
                className="flex w-full items-center justify-between rounded-xl border border-gray-700 bg-gray-800 px-4 py-4 text-left active:bg-gray-700"
              >
                <span>
                  <span className="block text-lg font-semibold tracking-wide">{p.name}</span>
                  {(p.partner || p.origin) && (
                    <span className="block text-sm text-gray-400">{p.partner ?? p.origin}</span>
                  )}
                </span>
                <span className="ml-3 shrink-0 rounded-full bg-gray-700 px-2 py-1 text-xs uppercase text-gray-300">
                  {p.state}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        onClick={() => setShowManual((s) => !s)}
        className="mt-6 w-fit text-sm text-gray-400 underline active:text-white"
      >
        {showManual ? "Hide manual entry" : "Enter a reference manually"}
      </button>
      {showManual && (
        <form onSubmit={handleSubmit} className="mt-3 space-y-3">
          <input
            type="text"
            placeholder={placeholder}
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            className={`w-full rounded-xl border border-gray-700 bg-gray-800 px-4 py-4 text-center text-lg tracking-wider text-white placeholder-gray-500 focus:outline-none ${focus}`}
          />
          <button
            type="submit"
            disabled={!ref.trim()}
            className={`w-full rounded-xl py-4 text-lg font-semibold disabled:opacity-40 ${btn}`}
          >
            Open Picking
          </button>
        </form>
      )}
    </div>
  );
}
